import iconHomeShapes from '../assets/art/icon-home-shapes.svg?raw'
import type { ColorKind, ShapeKind } from '../assets/art/ids'
import { ART } from '../assets/art/map'
import { svgFromRaw } from '../assets/art/svg'
import { SFX, VOICE } from '../audio/catalog'
import { el } from '../dom'
import { createPointerLatch, isFirstPointer } from '../input/single-pointer'
import { loadColorsLevel, saveColorsLevel } from '../storage/settings'
import {
  advanceLevel,
  artKeyFor,
  levelById,
  piecesForBoard,
  type LevelDef,
  type LevelPiece,
  type LevelTray,
} from './colors-levels'
import {
  CELEBRATE,
  CONFETTI,
  confettiOpacity,
  createConfettiParticle,
  DRAG_THRESHOLD_PX,
  dropOnTray,
  hitTrayIndex,
  HINT_REDUCED_MS,
  HINT_WIGGLE_MS,
  IDLE_HINT_MAX,
  IDLE_HINT_MS,
  isLevelComplete,
  layoutPlacedPieces,
  pickHintPiece,
  SNAP_PX,
  stepConfettiParticle,
} from './colors-logic'
import { matchVoiceClip } from './colors-voice'
import type { GameModule, Unmount } from './types'

const COLOR_VOICE: Record<ColorKind, string> = {
  red: VOICE.mauDo,
  yellow: VOICE.mauVang,
  blue: VOICE.mauXanhDaTroi,
  green: VOICE.mauXanhLaCay,
}

const SHAPE_VOICE: Record<ShapeKind, string> = {
  circle: VOICE.hinhTron,
  square: VOICE.hinhVuong,
  triangle: VOICE.hinhTamGiac,
}

const PIECE_FILL: Record<ColorKind, string> = {
  red: '#C4452D',
  yellow: '#E8B84A',
  blue: '#5B8FD6',
  green: '#4F8F4A',
}

type Drag = {
  pointerId: number
  piece: HTMLElement
  startX: number
  startY: number
  dragging: boolean
}

export const colorsGame: GameModule = {
  id: 'colors',
  titleVi: 'Màu & hình',
  iconSrc: iconHomeShapes,
  playable: true,
  mount(root, ctx): Unmount {
    const latch = createPointerLatch()
    let drag: Drag | null = null
    let level: LevelDef = levelById(loadColorsLevel())
    let pieceNodes: HTMLElement[] = []
    let trayNodes: HTMLElement[] = []
    let placedCount = 0
    let celebrating = false
    let hintCount = 0
    let idleTimer = 0
    const queued: number[] = []
    let confettiStop: (() => void) | null = null

    const layout = el('div', {
      className: 'shapes-layout',
      testId: 'game-colors',
      attrs: { 'data-feedback': 'idle', 'data-locked': 'false' },
    })
    const pool = el('div', { className: 'shape-pool', testId: 'shape-pool' })
    const trayRow = el('div', { className: 'shape-trays', testId: 'shape-trays' })
    const confetti = el('canvas', {
      className: 'shapes-confetti',
      testId: 'shapes-confetti',
      attrs: { 'aria-hidden': 'true' },
    }) as HTMLCanvasElement
    layout.append(pool, trayRow, confetti)

    const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const clearQueued = () => {
      for (const id of queued) window.clearTimeout(id)
      queued.length = 0
      confettiStop?.()
      confettiStop = null
    }

    const after = (ms: number, fn: () => void) => {
      queued.push(window.setTimeout(fn, ms))
    }

    const clearHint = () => {
      for (const node of [...pieceNodes, ...trayNodes]) node.classList.remove('is-hint')
    }

    const armIdle = () => {
      window.clearTimeout(idleTimer)
      if (celebrating || hintCount >= IDLE_HINT_MAX) return
      idleTimer = window.setTimeout(runHint, IDLE_HINT_MS)
    }

    const runHint = () => {
      if (celebrating || drag?.dragging) return
      const candidates = pieceNodes.map((piece) => {
        const tray = matchingTray(piece)
        const pieceBox = piece.getBoundingClientRect()
        const trayBox = tray?.getBoundingClientRect()
        return {
          id: piece.dataset.pieceId ?? '',
          placed: piece.classList.contains('is-placed'),
          x: pieceBox.left + pieceBox.width / 2,
          y: pieceBox.top + pieceBox.height / 2,
          trayX: trayBox ? trayBox.left + trayBox.width / 2 : 0,
          trayY: trayBox ? trayBox.top + trayBox.height / 2 : 0,
        }
      })
      const id = pickHintPiece(candidates)
      if (!id) return
      const piece = pieceNodes.find((node) => node.dataset.pieceId === id)
      const tray = piece ? matchingTray(piece) : undefined
      if (!piece || !tray) return
      hintCount += 1
      piece.classList.add('is-hint')
      tray.classList.add('is-hint')
      layout.dataset.feedback = 'hint'
      after(prefersReduced() ? HINT_REDUCED_MS : HINT_WIGGLE_MS, () => {
        piece.classList.remove('is-hint')
        tray.classList.remove('is-hint')
        if (layout.dataset.feedback === 'hint') layout.dataset.feedback = 'idle'
        armIdle()
      })
    }

    const matchingTray = (piece: HTMLElement): HTMLElement | undefined => {
      return trayNodes.find((tray) => trayMatchesPiece(tray, piece))
    }

    const speakPiece = (piece: HTMLElement) => {
      const color = piece.dataset.color
      const shape = piece.dataset.shape
      if (!isColor(color) || !isShape(shape)) return
      layout.dataset.feedback = 'label'
      ctx.audio.playFeedback(SFX.tap, [COLOR_VOICE[color], SHAPE_VOICE[shape]])
    }

    const homePiece = (piece: HTMLElement) => {
      const current = piece.style.transform
      piece.classList.remove('is-dragging')
      piece.style.transform = current
      void piece.offsetWidth
      piece.style.transform = ''
    }

    const applyDrop = (piece: HTMLElement) => {
      const shape = piece.dataset.shape
      const color = piece.dataset.color
      if (!isShape(shape) || !isColor(color)) {
        homePiece(piece)
        return
      }
      const pieceBox = piece.getBoundingClientRect()
      const trayBoxes = trayNodes.map((tray) => tray.getBoundingClientRect())
      const index = hitTrayIndex(pieceBox, trayBoxes, SNAP_PX)
      if (index === null) {
        homePiece(piece)
        ctx.audio.play(SFX.tap)
        return
      }
      const tray = trayNodes[index]
      if (!tray) {
        homePiece(piece)
        ctx.audio.play(SFX.tap)
        return
      }
      const traySpec = trayFromNode(tray)
      if (!traySpec) {
        homePiece(piece)
        ctx.audio.play(SFX.tap)
        return
      }
      const result = dropOnTray({ shape, color }, traySpec)
      if (result !== 'match') {
        homePiece(piece)
        ctx.audio.play(SFX.tap)
        layout.dataset.feedback = 'return'
        return
      }
      snapPieceIntoTray(piece, tray)
      placedCount += 1
      layout.dataset.feedback = 'match'
      const last = isLevelComplete(placedCount, pieceNodes.length)
      if (last) {
        ctx.audio.playFeedback(SFX.sparkle)
        startCelebrate()
        return
      }
      ctx.audio.playFeedback(SFX.sparkle, [matchVoiceClip(shape, color, level.sortBy)])
    }

    const startCelebrate = () => {
      celebrating = true
      layout.dataset.locked = 'true'
      layout.dataset.feedback = 'celebrate'
      window.clearTimeout(idleTimer)
      clearQueued()
      clearHint()
      const next = advanceLevel(level.id)
      saveColorsLevel(next.level)
      const reduced = prefersReduced()
      after(CELEBRATE.voiceAtMs, () => {
        ctx.audio.play(SFX.bell, { gain: 0.45 })
        ctx.audio.play(VOICE.gioiQua)
      })
      if (reduced) {
        after(CELEBRATE.reducedHoldMs, () => {
          layout.classList.add('is-exiting')
        })
        after(CELEBRATE.reducedHoldMs + CELEBRATE.fadeMs, () => {
          mountLevel(next.level, next.shuffle, false)
          unlockBoard()
        })
        return
      }
      after(CELEBRATE.hopAtMs, hopTrays)
      after(CELEBRATE.confettiStartMs, () => {
        confettiStop = burstConfetti(confetti, layout, trayRow, pieceColors())
      })
      after(CELEBRATE.fadeAtMs, () => {
        layout.classList.add('is-exiting')
      })
      after(CELEBRATE.nextAtMs, () => {
        mountLevel(next.level, next.shuffle, true)
      })
      after(CELEBRATE.totalMs, unlockBoard)
    }

    const hopTrays = () => {
      for (const [index, tray] of trayNodes.entries()) {
        after(index * CELEBRATE.hopStaggerMs, () => {
          tray.classList.remove('is-hop')
          void tray.offsetWidth
          tray.classList.add('is-hop')
        })
      }
    }

    const pieceColors = (): string[] => {
      const colors = new Set<string>()
      for (const piece of pieceNodes) {
        const color = piece.dataset.color
        if (isColor(color)) colors.add(PIECE_FILL[color])
      }
      return [...colors]
    }

    const unlockBoard = () => {
      celebrating = false
      layout.dataset.locked = 'false'
      layout.classList.remove('is-exiting')
      layout.dataset.feedback = 'idle'
      armIdle()
    }

    const mountLevel = (id: number, shuffle: boolean, entering: boolean) => {
      clearHint()
      confettiStop?.()
      confettiStop = null
      const ctx2d = confetti.getContext('2d')
      ctx2d?.clearRect(0, 0, confetti.width, confetti.height)
      level = levelById(id)
      placedCount = 0
      hintCount = 0
      layout.classList.remove('is-exiting')
      layout.dataset.level = String(level.id)
      layout.dataset.sort = level.sortBy
      layout.dataset.feedback = entering ? 'enter' : 'idle'
      pool.replaceChildren()
      trayRow.replaceChildren()
      trayNodes = level.trays.map((spec) => makeTray(spec))
      for (const tray of trayNodes) trayRow.append(tray)
      const specs = piecesForBoard(level, shuffle)
      pieceNodes = specs.map((spec) => makePiece(spec))
      pool.dataset.count = String(pieceNodes.length)
      for (const [index, piece] of pieceNodes.entries()) {
        if (entering) {
          piece.classList.add('is-enter')
          piece.style.animationDelay = `${index * CELEBRATE.enterStaggerMs}ms`
          piece.addEventListener(
            'animationend',
            () => {
              piece.classList.remove('is-enter')
              piece.style.animationDelay = ''
            },
            { once: true },
          )
        }
        pool.append(piece)
      }
    }

    const onDown = (event: PointerEvent) => {
      if (celebrating || layout.dataset.locked === 'true') return
      if (!isFirstPointer(event, latch)) return
      clearHint()
      armIdle()
      const target = event.target
      if (!(target instanceof Element)) {
        latch.release(event.pointerId)
        return
      }
      const piece = target.closest('.shape-piece')
      if (!(piece instanceof HTMLElement) || piece.classList.contains('is-placed')) {
        latch.release(event.pointerId)
        return
      }
      event.preventDefault()
      try {
        piece.setPointerCapture(event.pointerId)
      } catch {
        // ignore
      }
      drag = {
        pointerId: event.pointerId,
        piece,
        startX: event.clientX,
        startY: event.clientY,
        dragging: false,
      }
    }

    const onMove = (event: PointerEvent) => {
      if (!drag || !latch.owns(event.pointerId)) return
      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      if (!drag.dragging && Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
        drag.dragging = true
        drag.piece.classList.add('is-dragging')
        window.clearTimeout(idleTimer)
        clearHint()
      }
      if (!drag.dragging) return
      event.preventDefault()
      drag.piece.style.transform = `translate(${dx}px, ${dy}px)`
    }

    const onUp = (event: PointerEvent) => {
      if (!latch.owns(event.pointerId)) return
      latch.release(event.pointerId)
      const current = drag
      drag = null
      if (!current) return
      if (celebrating) {
        homePiece(current.piece)
        return
      }
      if (current.dragging) applyDrop(current.piece)
      else speakPiece(current.piece)
      armIdle()
    }

    layout.addEventListener('pointerdown', onDown)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)

    root.append(layout)
    mountLevel(level.id, false, false)
    armIdle()

    return () => {
      window.clearTimeout(idleTimer)
      clearQueued()
      latch.reset()
      layout.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      root.replaceChildren()
    }
  },
}

function makePiece(spec: LevelPiece): HTMLElement {
  const key = artKeyFor(spec.shape, spec.color)
  const node = el(
    'button',
    {
      className: `shape-piece${spec.size === 'big' ? ' is-big' : ''}`,
      testId: `shape-piece-${spec.id}`,
      attrs: {
        type: 'button',
        'data-shape': spec.shape,
        'data-color': spec.color,
        'data-piece-id': spec.id,
        draggable: 'false',
      },
    },
    [svgFromRaw(ART.shapes[key], 'shape-piece__img')],
  )
  node.style.setProperty('--piece-scale', String(spec.scale))
  node.style.setProperty('--piece-rotate', `${spec.rotateDeg}deg`)
  return node
}

function makeTray(spec: LevelTray): HTMLElement {
  const sparkle = el('div', { className: 'shape-tray__sparkle', attrs: { 'aria-hidden': 'true' } })
  const slots = el('div', { className: 'shape-tray__slots' })
  const mark = spec.kind === 'color' ? colorMark(spec.color) : holeOutline(spec.shape)
  const attrs: Record<string, string> = {
    'data-kind': spec.kind,
    'data-filled': 'false',
    'data-count': '0',
  }
  if (spec.kind === 'shape') attrs['data-shape'] = spec.shape
  else attrs['data-color'] = spec.color
  return el(
    'div',
    {
      className: `shape-tray shape-tray--${spec.kind}`,
      testId: spec.kind === 'shape' ? `shape-tray-${spec.shape}` : `color-tray-${spec.color}`,
      attrs,
    },
    [mark, sparkle, slots],
  )
}

function colorMark(color: 'red' | 'yellow' | 'blue'): SVGSVGElement {
  const svg = svgFromRaw(ART.colorTrays[color], 'shape-tray__mark')
  svg.setAttribute('aria-hidden', 'true')
  return svg
}

function holeOutline(shape: ShapeKind): SVGSVGElement {
  const hole = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  hole.setAttribute('class', 'shape-tray__hole')
  hole.setAttribute('viewBox', '0 0 100 100')
  hole.setAttribute('aria-hidden', 'true')
  hole.append(holePath(shape))
  return hole
}

function holePath(shape: ShapeKind): SVGElement {
  switch (shape) {
    case 'circle': {
      const node = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      node.setAttribute('cx', '50')
      node.setAttribute('cy', '50')
      node.setAttribute('r', '34')
      node.setAttribute('fill', 'none')
      node.setAttribute('stroke', 'currentColor')
      node.setAttribute('stroke-width', '8')
      return node
    }
    case 'square': {
      const node = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      node.setAttribute('x', '18')
      node.setAttribute('y', '18')
      node.setAttribute('width', '64')
      node.setAttribute('height', '64')
      node.setAttribute('rx', '10')
      node.setAttribute('fill', 'none')
      node.setAttribute('stroke', 'currentColor')
      node.setAttribute('stroke-width', '8')
      return node
    }
    case 'triangle': {
      const node = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      node.setAttribute('d', 'M50 16 L84 80 L16 80 Z')
      node.setAttribute('fill', 'none')
      node.setAttribute('stroke', 'currentColor')
      node.setAttribute('stroke-width', '8')
      node.setAttribute('stroke-linejoin', 'round')
      return node
    }
    default: {
      const _never: never = shape
      return _never
    }
  }
}

function snapPieceIntoTray(piece: HTMLElement, tray: HTMLElement): void {
  const slots = tray.querySelector('.shape-tray__slots')
  const first = piece.getBoundingClientRect()
  piece.classList.remove('is-dragging')
  piece.style.transform = ''
  piece.classList.add('is-placed')
  const host = slots instanceof HTMLElement ? slots : tray
  host.append(piece)
  const count = host.querySelectorAll('.shape-piece.is-placed').length
  tray.dataset.filled = 'true'
  tray.dataset.count = String(count)
  tray.classList.add('is-filled', 'is-celebrate')
  layoutTraySlots(tray)
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  const last = piece.getBoundingClientRect()
  const dx = first.left + first.width / 2 - (last.left + last.width / 2)
  const dy = first.top + first.height / 2 - (last.top + last.height / 2)
  const sx = last.width > 0 ? first.width / last.width : 1
  const sy = last.height > 0 ? first.height / last.height : 1
  piece.style.transition = 'none'
  piece.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`
  void piece.offsetWidth
  piece.style.transition = ''
  piece.classList.add('is-snapping')
  piece.style.transform = ''
  piece.addEventListener(
    'transitionend',
    () => {
      piece.classList.remove('is-snapping')
    },
    { once: true },
  )
}

function layoutTraySlots(tray: HTMLElement): void {
  const slots = tray.querySelector('.shape-tray__slots')
  const host = slots instanceof HTMLElement ? slots : tray
  const pieces = [...host.querySelectorAll<HTMLElement>('.shape-piece.is-placed')]
  const layouts = layoutPlacedPieces(pieces.length, host.clientWidth, host.clientHeight)
  for (const [index, piece] of pieces.entries()) {
    const next = layouts[index]
    if (!next) continue
    piece.style.setProperty('--placed-left', `${next.left}px`)
    piece.style.setProperty('--placed-top', `${next.top}px`)
    piece.style.setProperty('--placed-size', `${next.size}px`)
    piece.style.setProperty('--placed-rotate', `${next.rotateDeg}deg`)
    piece.style.zIndex = String(index + 1)
  }
}

function trayFromNode(tray: HTMLElement): LevelTray | null {
  const kind = tray.dataset.kind
  if (kind === 'shape') {
    const shape = tray.dataset.shape
    if (!isShape(shape)) return null
    return { kind: 'shape', shape }
  }
  if (kind === 'color') {
    const color = tray.dataset.color
    if (color !== 'red' && color !== 'yellow' && color !== 'blue') return null
    return { kind: 'color', color }
  }
  return null
}

function trayMatchesPiece(tray: HTMLElement, piece: HTMLElement): boolean {
  const spec = trayFromNode(tray)
  const shape = piece.dataset.shape
  const color = piece.dataset.color
  if (!spec || !isShape(shape) || !isColor(color)) return false
  return dropOnTray({ shape, color }, spec) === 'match'
}

function burstConfetti(
  canvas: HTMLCanvasElement,
  layout: HTMLElement,
  trayRow: HTMLElement,
  colors: readonly string[],
): () => void {
  const palette = colors.length ? colors : ['#C4452D', '#E8B84A', '#5B8FD6']
  const layoutBox = layout.getBoundingClientRect()
  const trayBox = trayRow.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1
  const cssW = layoutBox.width
  const cssH = layoutBox.height
  canvas.width = Math.max(1, Math.round(cssW * dpr))
  canvas.height = Math.max(1, Math.round(cssH * dpr))
  canvas.style.width = `${cssW}px`
  canvas.style.height = `${cssH}px`
  const ctx2d = canvas.getContext('2d')
  if (!ctx2d) return () => {}
  ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0)

  const origin = {
    x: trayBox.left - layoutBox.left + trayBox.width / 2,
    y: trayBox.top - layoutBox.top + trayBox.height / 2,
  }
  const particles = Array.from({ length: CONFETTI.count }, (_, i) => {
    return createConfettiParticle(origin, palette, i)
  })

  const bounds = { width: cssW, height: cssH }
  const started = performance.now()
  const duration = CELEBRATE.confettiEndMs - CELEBRATE.confettiStartMs
  let last = started
  let raf = 0
  let stopped = false

  const frame = (now: number) => {
    if (stopped) return
    const t = now - started
    const dt = Math.min(0.032, Math.max(0, (now - last) / 1000))
    last = now
    const fade = confettiOpacity(t, duration)
    ctx2d.clearRect(0, 0, cssW, cssH)
    for (const particle of particles) {
      stepConfettiParticle(particle, dt, bounds)
      ctx2d.save()
      ctx2d.globalAlpha = fade
      ctx2d.translate(particle.x, particle.y)
      ctx2d.rotate(particle.rot)
      ctx2d.fillStyle = particle.color
      drawConfettiShape(ctx2d, particle.kind, particle.size)
      ctx2d.restore()
    }
    if (t < duration) raf = window.requestAnimationFrame(frame)
  }
  raf = window.requestAnimationFrame(frame)
  return () => {
    stopped = true
    window.cancelAnimationFrame(raf)
    ctx2d.clearRect(0, 0, cssW, cssH)
  }
}

function drawConfettiShape(
  ctx2d: CanvasRenderingContext2D,
  kind: 'circle' | 'square' | 'triangle',
  size: number,
): void {
  const half = size / 2
  switch (kind) {
    case 'circle':
      ctx2d.beginPath()
      ctx2d.arc(0, 0, half, 0, Math.PI * 2)
      ctx2d.fill()
      return
    case 'square':
      ctx2d.fillRect(-half, -half, size, size)
      return
    case 'triangle':
      ctx2d.beginPath()
      ctx2d.moveTo(0, -half)
      ctx2d.lineTo(half, half)
      ctx2d.lineTo(-half, half)
      ctx2d.closePath()
      ctx2d.fill()
      return
    default: {
      const _never: never = kind
      return _never
    }
  }
}

function isColor(value: string | undefined): value is ColorKind {
  return value === 'red' || value === 'yellow' || value === 'blue' || value === 'green'
}

function isShape(value: string | undefined): value is ShapeKind {
  return value === 'circle' || value === 'square' || value === 'triangle'
}
